"use client"

/** Lillyy-inspired Sahil hero slider: drag/swipe navigation with circular pagination and frontend-only bilingual copy. */
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { ArrowUpRightMini } from "@medusajs/icons"
import Image from "next/image"
import type { HeroBanner } from "@lib/data/hero-banners"
import { useFrontendPreferences } from "@lib/frontend-preferences"
import {
  KeyboardEvent,
  PointerEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"

const mediaSlides = [
  {
    href: "/store",
    image:
      "https://files.manuscdn.com/user_upload_by_module/session_file/310519663366518963/LDQeQSEhXGfGpuGx.jpg",
    video:
      "https://files.manuscdn.com/user_upload_by_module/session_file/310519663366518963/phucNgCXFpXWijFQ.mp4",
    imageClass: "sahil-home-slider__image--tide",
  },
  {
    href: "/store",
    image:
      "https://files.manuscdn.com/user_upload_by_module/session_file/310519663366518963/qnTAnoXiDlvEfmFv.jpg",
    imageClass: "sahil-home-slider__image--pearl",
  },
  {
    href: "/store",
    image:
      "https://files.manuscdn.com/user_upload_by_module/session_file/310519663366518963/iuhbjKkMtCCmGmjT.jpg",
    imageClass: "sahil-home-slider__image--shell",
  },
  {
    href: "/store",
    image:
      "https://files.manuscdn.com/user_upload_by_module/session_file/310519663366518963/HOGalJUiagBbAvRS.jpg",
    imageClass: "sahil-home-slider__image--pearl",
  },
  {
    href: "/store",
    image:
      "https://files.manuscdn.com/user_upload_by_module/session_file/310519663366518963/yTrKqUqQVXqeCGsI.jpg",
    imageClass: "sahil-home-slider__image--tide",
  },
  {
    href: "/store",
    image:
      "https://files.manuscdn.com/user_upload_by_module/session_file/310519663366518963/iYQBZrjOnpDjWesi.jpg",
    imageClass: "sahil-home-slider__image--shell",
  },
  {
    href: "/collections",
    image:
      "https://files.manuscdn.com/user_upload_by_module/session_file/310519663366518963/iuhbjKkMtCCmGmjT.jpg",
    video:
      "https://files.manuscdn.com/user_upload_by_module/session_file/310519663366518963/cotnFWivIpyzdfWr.mp4",
    imageClass: "sahil-home-slider__image--shell",
  },
  {
    href: "/collections",
    image: "/assets/sahil/sahil-shell-sculpture.png",
    imageClass: "sahil-home-slider__image--sculpture",
  },
]

type HeroProps = {
  banners?: HeroBanner[]
}

const Hero = ({ banners }: HeroProps) => {
  const { locale, t } = useFrontendPreferences()
  const slides = useMemo(
    () => {
      if (banners && banners.length > 0) {
        return banners.map((banner) => ({
          href: banner.href,
          image: banner.image,
          video: banner.video || undefined,
          imageClass: banner.image_class,
          eyebrow: locale === "fa" ? banner.eyebrow_fa : banner.eyebrow_en,
          title: locale === "fa" ? banner.title_fa : banner.title_en,
          body: locale === "fa" ? banner.body_fa : banner.body_en,
          cta: locale === "fa" ? banner.cta_fa : banner.cta_en,
        }))
      }

      return mediaSlides.map((media, index) => ({
        ...media,
        ...t.hero.slides[index],
      }))
    },
    [banners, locale, t.hero.slides]
  )
  const [activeSlide, setActiveSlide] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const dragStartX = useRef<number | null>(null)
  const dragDistance = useRef(0)

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActiveSlide((current) => (current + 1) % slides.length)
    }, 6500)
    return () => window.clearInterval(timer)
  }, [activeSlide, slides.length])

  const goToPrevious = () =>
    setActiveSlide((current) => (current - 1 + slides.length) % slides.length)
  const goToNext = () =>
    setActiveSlide((current) => (current + 1) % slides.length)
  const resetPointer = () => {
    if (Math.abs(dragDistance.current) > 44) {
      if (dragDistance.current < 0) {
        goToNext()
      } else {
        goToPrevious()
      }
    }
    dragStartX.current = null
    dragDistance.current = 0
    setIsDragging(false)
  }
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "ArrowLeft") {
      event.preventDefault()
      goToPrevious()
    }
    if (event.key === "ArrowRight") {
      event.preventDefault()
      goToNext()
    }
  }

  return (
    <section
      className={`sahil-home-slider sahil-home-slider--${locale}`}
      aria-label={t.hero.ariaLabel}
    >
      <div
        className={`sahil-home-slider__slides ${isDragging ? "is-dragging" : ""}`}
        aria-live="polite"
        tabIndex={0}
        onKeyDown={handleKeyDown}
        onPointerDown={(event: PointerEvent<HTMLDivElement>) => {
          dragStartX.current = event.clientX
          dragDistance.current = 0
          setIsDragging(true)
          event.currentTarget.setPointerCapture(event.pointerId)
        }}
        onPointerMove={(event: PointerEvent<HTMLDivElement>) => {
          if (dragStartX.current !== null)
            dragDistance.current = event.clientX - dragStartX.current
        }}
        onPointerUp={resetPointer}
        onPointerCancel={resetPointer}
      >
        {slides.map((slide, index) => (
          <article
            key={`${slide.title}-${index}`}
            className={`sahil-home-slider__slide ${index === activeSlide ? "is-active" : ""}`}
            aria-hidden={index !== activeSlide}
          >
            {slide.video ? (
              <>
                <Image
                  src={slide.image}
                  alt=""
                  fill
                  sizes="100vw"
                  priority={index === 0}
                  draggable={false}
                  className={`sahil-home-slider__image ${slide.imageClass}`}
                />
                <video
                  className={`sahil-home-slider__video ${slide.imageClass}`}
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload={index === 0 ? "auto" : "metadata"}
                  aria-hidden="true"
                  onError={(event) => {
                    event.currentTarget.style.opacity = "0"
                  }}
                >
                  <source src={slide.video} type="video/mp4" />
                </video>
              </>
            ) : (
              <Image
                src={slide.image}
                alt=""
                fill
                sizes="100vw"
                priority={index === 0}
                draggable={false}
                className={`sahil-home-slider__image ${slide.imageClass}`}
              />
            )}
            <div className="sahil-home-slider__wash" />
            <div className="sahil-home-slider__content content-container">
              <p className="sahil-home-slider__eyebrow">{slide.eyebrow}</p>
              <h1>{slide.title}</h1>
              <p className="sahil-home-slider__body">{slide.body}</p>
              <LocalizedClientLink
                href={slide.href}
                className="sahil-home-slider__cta"
                tabIndex={index === activeSlide ? 0 : -1}
              >
                <span>{slide.cta}</span>
                <ArrowUpRightMini aria-hidden="true" />
              </LocalizedClientLink>
            </div>
          </article>
        ))}
      </div>
      <div className="sahil-home-slider__controls content-container">
        <div
          className="sahil-home-slider__pagination"
          role="tablist"
          aria-label={t.hero.chooseSlide}
        >
          {slides.map((slide, index) => (
            <button
              key={`${slide.title}-${index}`}
              type="button"
              role="tab"
              aria-selected={index === activeSlide}
              aria-label={`${t.hero.chooseSlide} ${index + 1}`}
              className={index === activeSlide ? "is-active" : ""}
              onClick={() => setActiveSlide(index)}
            />
          ))}
        </div>
        <span className="sahil-home-slider__drag-hint">{t.hero.dragHint}</span>
      </div>
    </section>
  )
}

export default Hero
